import { TAU, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { RenderTarget, TechniqueContext } from '../types';
import type { GameOfLifeValues } from './params';

/**
 * Two-state cellular automaton seeded from the text.
 *
 * Rules are stored as bitmasks over the neighbour count — bit *n* set means "this
 * happens with n live neighbours". That makes an arbitrary B/S notation a parse plus
 * two shifts in the inner loop, instead of a switch per cell per generation.
 *
 * `age` records the last generation in which a cell was alive, which is what lets the
 * "all generations" view colour the history without keeping every grid.
 */
export interface GameOfLifeState {
  cells: Uint8Array;
  next: Uint8Array;
  /** Last generation each cell was alive in; 0 means never. */
  age: Uint16Array;
  fixed: Uint8Array;
  blocked: Uint8Array;
  cols: number;
  rows: number;
  cell: number;
  birth: number;
  survive: number;
  generation: number;
  palette: ResolvedPalette;
  targetGenerations: number;
}

const PRESETS: Record<string, string> = {
  conway: 'B3/S23',
  highlife: 'B36/S23',
  daynight: 'B3678/S34678',
  seeds: 'B2/S',
  maze: 'B3/S12345',
};

/** Parses `B3/S23` into two 9-bit masks. */
function parseRule(notation: string): { birth: number; survive: number } {
  const match = /B([0-8]*)\s*\/\s*S([0-8]*)/i.exec(notation.trim());
  if (!match) return { birth: 1 << 3, survive: (1 << 2) | (1 << 3) };
  let birth = 0;
  let survive = 0;
  for (const d of match[1]) birth |= 1 << Number(d);
  for (const d of match[2]) survive |= 1 << Number(d);
  return { birth, survive };
}

function values(ctx: TechniqueContext): GameOfLifeValues {
  return ctx.values as unknown as GameOfLifeValues;
}

export function init(ctx: TechniqueContext): GameOfLifeState {
  const v = values(ctx);
  const cell = Math.max(1, v.cell);
  const cols = Math.max(1, Math.ceil(ctx.width / cell));
  const rows = Math.max(1, Math.ceil(ctx.height / cell));
  const size = cols * rows;

  const rule = parseRule(v.ruleset === 'custom' ? v.customRule : (PRESETS[v.ruleset] ?? 'B3/S23'));

  const state: GameOfLifeState = {
    cells: new Uint8Array(size),
    next: new Uint8Array(size),
    age: new Uint16Array(size),
    fixed: new Uint8Array(size),
    blocked: new Uint8Array(size),
    cols,
    rows,
    cell,
    birth: rule.birth,
    survive: rule.survive,
    generation: 0,
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    targetGenerations: v.generations,
  };

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      const inside = ctx.field.coverage((c + 0.5) * cell, (r + 0.5) * cell) > 0.5;

      let alive = false;
      if (v.textRole === 'outside') alive = !inside;
      else alive = inside;

      if (v.textRole === 'fixed' && inside) state.fixed[i] = 1;
      if (v.textRole === 'blocked' && inside) {
        state.blocked[i] = 1;
        alive = false;
      }

      if (!alive && v.noise > 0 && !state.blocked[i]) alive = ctx.rng.chance(v.noise);

      state.cells[i] = alive ? 1 : 0;
      if (alive) state.age[i] = 1;
    }
  }

  return state;
}

export function step(state: GameOfLifeState, _dt: number, ctx: TechniqueContext): GameOfLifeState {
  if (state.generation >= state.targetGenerations) return state;
  const v = values(ctx);

  const perFrame = Math.max(1, Math.round(3 * clamp01(ctx.quality)));
  const { cols, rows, cells, next, age, birth, survive } = state;

  for (let f = 0; f < perFrame && state.generation < state.targetGenerations; f++) {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c;
        if (state.blocked[i]) {
          next[i] = 0;
          continue;
        }
        if (state.fixed[i]) {
          next[i] = 1;
          continue;
        }

        let live = 0;
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            if (dr === 0 && dc === 0) continue;
            let nr = r + dr;
            let nc = c + dc;
            if (v.wrap) {
              nr = (nr + rows) % rows;
              nc = (nc + cols) % cols;
            } else if (nr < 0 || nc < 0 || nr >= rows || nc >= cols) continue;
            live += cells[nr * cols + nc];
          }
        }

        const alive = cells[i] === 1;
        next[i] = (alive ? (survive >> live) & 1 : (birth >> live) & 1) as number;
      }
    }

    cells.set(next);
    state.generation++;
    for (let i = 0; i < cells.length; i++) if (cells[i]) age[i] = state.generation;
  }

  return state;
}

export function isComplete(state: GameOfLifeState): boolean {
  return state.generation >= state.targetGenerations;
}

export function draw(state: GameOfLifeState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const size = state.cell * (1 - v.gap);
  const offset = (state.cell - size) / 2;

  // One path per colour bucket rather than per cell.
  const buckets = new Map<string, Path2D>();

  for (let r = 0; r < state.rows; r++) {
    for (let col = 0; col < state.cols; col++) {
      const i = r * state.cols + col;
      const alive = state.cells[i] === 1;
      const lastSeen = state.age[i];

      if (v.display === 'current' ? !alive : lastSeen === 0) continue;

      const t = v.display === 'current' ? 1 : lastSeen / Math.max(1, state.generation);
      const color = state.palette.at(t, v.opacity);
      let path = buckets.get(color);
      if (!path) {
        path = new Path2D();
        buckets.set(color, path);
      }

      const x = col * state.cell + offset;
      const y = r * state.cell + offset;
      if (v.round) {
        path.moveTo(x + size, y + size / 2);
        path.arc(x + size / 2, y + size / 2, size / 2, 0, TAU);
      } else {
        path.rect(x, y, size, size);
      }
    }
  }

  for (const [color, path] of buckets) {
    c.fillStyle = color;
    c.fill(path);
  }
}

export function elementCount(state: GameOfLifeState): number {
  let n = 0;
  for (let i = 0; i < state.cells.length; i++) if (state.cells[i]) n++;
  return n;
}
